import React, { useState, useEffect } from 'react';
import { 
  Eye, EyeOff, Plus, Trash2, Loader2, Save, Settings
} from 'lucide-react';
import { suiviConfigAPI } from '../../services/api';

const COULEURS_DISPONIBLES = [
  { value: 'bg-green-500', label: 'Vert', preview: '#22c55e' },
  { value: 'bg-green-600', label: 'Vert foncé', preview: '#16a34a' },
  { value: 'bg-blue-500', label: 'Bleu', preview: '#3b82f6' },
  { value: 'bg-blue-600', label: 'Bleu foncé', preview: '#2563eb' },
  { value: 'bg-purple-500', label: 'Violet', preview: '#a855f7' },
  { value: 'bg-pink-500', label: 'Rose', preview: '#ec4899' },
  { value: 'bg-orange-500', label: 'Orange', preview: '#f97316' },
  { value: 'bg-yellow-500', label: 'Jaune', preview: '#eab308' },
  { value: 'bg-red-500', label: 'Rouge', preview: '#ef4444' },
  { value: 'bg-teal-500', label: 'Turquoise', preview: '#14b8a6' },
  { value: 'bg-indigo-500', label: 'Indigo', preview: '#6366f1' },
  { value: 'bg-amber-500', label: 'Ambre', preview: '#f59e0b' },
  { value: 'bg-cyan-500', label: 'Cyan', preview: '#06b6d4' },
  { value: 'bg-emerald-500', label: 'Émeraude', preview: '#10b981' },
  { value: 'bg-violet-500', label: 'Violet', preview: '#8b5cf6' },
  { value: 'bg-rose-500', label: 'Rose', preview: '#f43f5e' },
  { value: 'bg-gray-500', label: 'Gris', preview: '#6b7280' },
];

const CATEGORIES = [
  { value: 'systeme', label: '⚙️ Système' },
  { value: 'communication', label: '📞 Communication' },
  { value: 'document', label: '📄 Document' },
  { value: 'autre', label: '📌 Autre' },
];

const EMOJIS = [
  '📞', '📲', '📧', '📩', '✉️', '📬', '💬', '📥', '📤', '✅', '❌',
  '📅', '🤝', '⏰', '📝', '📌', '🆕', '🔄', '👤', '✏️', '📋', '📄',
  '🔔', '⚡', '🎯', '💼', '📊', '🔍', '💡', '⭐', '🏠', '💰'
];

/**
 * Widget de configuration des types d'actions de suivi
 */
const WidgetSuiviConfig = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [newType, setNewType] = useState({
    code: '',
    label: '',
    icone: '📌',
    couleur: 'bg-gray-500',
    categorie: 'autre'
  });

  useEffect(() => {
    loadTypes();
  }, []);

  const loadTypes = async () => {
    try {
      setLoading(true);
      const res = await suiviConfigAPI.getAllTypes();
      setTypes(res.data);
      
      // Si vide, initialiser avec les valeurs par défaut
      if (res.data.length === 0) {
        await suiviConfigAPI.initDefaults();
        const resInit = await suiviConfigAPI.getAllTypes();
        setTypes(resInit.data);
      }
    } catch (error) {
      console.error('Erreur chargement types:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleVisibility = async (type) => {
    setSaving(type.id);
    try {
      await suiviConfigAPI.updateType(type.id, { visible: !type.visible });
      await loadTypes();
    } catch (error) {
      console.error('Erreur toggle:', error);
    } finally {
      setSaving(null);
    }
  };

  const handleAddType = async (e) => {
    e.preventDefault();
    if (!newType.code || !newType.label) return;
    
    setSaving('new');
    try {
      await suiviConfigAPI.createType({
        ...newType,
        code: newType.code.toLowerCase().replace(/\s+/g, '_')
      });
      await loadTypes();
      setNewType({ code: '', label: '', icone: '📌', couleur: 'bg-gray-500', categorie: 'autre' });
      setShowAddForm(false);
    } catch (error) {
      console.error('Erreur création:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(null);
    }
  };

  const handleDeleteType = async (typeId) => {
    if (!window.confirm('Supprimer ce type d\'action ?')) return;
    
    setSaving(typeId);
    try {
      await suiviConfigAPI.deleteType(typeId);
      await loadTypes();
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setSaving(null);
    }
  };

  const handleUpdateType = async (typeId, updates) => {
    setSaving(typeId);
    try {
      await suiviConfigAPI.updateType(typeId, updates);
      await loadTypes();
      setEditingType(null);
    } catch (error) {
      console.error('Erreur mise à jour:', error);
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Grouper par catégorie
  const typesByCategory = types.reduce((acc, type) => {
    const cat = type.categorie || 'autre';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(type);
    return acc;
  }, {});

  const visibleCount = types.filter(t => t.visible).length;

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="flex items-center gap-4 text-sm">
        <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full">
          {visibleCount}/{types.length} types visibles
        </span>
        <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full">
          {types.filter(t => t.systeme).length} système
        </span>
        <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full">
          {types.filter(t => !t.systeme).length} personnalisés
        </span>
      </div>

      {/* Types par catégorie */}
      {CATEGORIES.map(cat => {
        const catTypes = typesByCategory[cat.value] || [];
        if (catTypes.length === 0) return null;
        
        return (
          <div key={cat.value} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <h4 className="font-semibold text-gray-900">{cat.label}</h4>
            </div>
            <div className="p-3 space-y-2">
              {catTypes.map(type => (
                <div 
                  key={type.id}
                  className={`flex items-center justify-between p-3 rounded-lg transition ${
                    type.visible ? 'bg-gray-50' : 'bg-gray-100 opacity-60'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className={`w-10 h-10 rounded-full ${type.couleur} flex items-center justify-center text-lg text-white shadow`}
                    >
                      {type.icone}
                    </div>
                    <div>
                      <span className={`font-medium ${type.visible ? 'text-gray-900' : 'text-gray-500'}`}>
                        {type.label}
                      </span>
                      {type.systeme && (
                        <span className="ml-2 text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                          Système
                        </span>
                      )}
                      {!type.visible && (
                        <span className="ml-2 text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                          Masqué
                        </span>
                      )}
                      <div className="text-xs text-gray-400 mt-0.5">Code: {type.code}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {/* Éditer (si pas système) */}
                    {!type.systeme && (
                      <button
                        onClick={() => setEditingType(editingType === type.id ? null : type.id)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                    )}
                    
                    {/* Toggle visibilité */}
                    <button
                      onClick={() => toggleVisibility(type)}
                      disabled={saving === type.id}
                      className={`p-2 rounded-lg transition ${
                        type.visible 
                          ? 'text-green-600 hover:bg-green-50' 
                          : 'text-gray-400 hover:bg-gray-100'
                      }`}
                    >
                      {saving === type.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : type.visible ? (
                        <Eye className="w-4 h-4" />
                      ) : (
                        <EyeOff className="w-4 h-4" />
                      )}
                    </button>
                    
                    {/* Supprimer (si pas système) */}
                    {!type.systeme && (
                      <button
                        onClick={() => handleDeleteType(type.id)}
                        disabled={saving === type.id}
                        className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Formulaire édition */}
      {editingType && (
        <EditTypeForm 
          type={types.find(t => t.id === editingType)}
          onSave={(updates) => handleUpdateType(editingType, updates)}
          onCancel={() => setEditingType(null)}
          saving={saving === editingType}
        />
      )}

      {/* Formulaire ajout */}
      {showAddForm ? (
        <form onSubmit={handleAddType} className="bg-green-50 rounded-xl p-4 border border-green-200">
          <h4 className="font-semibold text-gray-900 mb-4">➕ Nouveau type d'action</h4>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Code *</label>
              <input
                type="text"
                value={newType.code}
                onChange={(e) => setNewType({ ...newType, code: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg text-sm"
                placeholder="appel_client"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Libellé *</label>
              <input
                type="text"
                value={newType.label}
                onChange={(e) => setNewType({ ...newType, label: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg text-sm"
                placeholder="Appel client"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Icône</label>
              <div className="flex flex-wrap gap-1 p-2 border rounded-lg bg-white max-h-24 overflow-y-auto">
                {EMOJIS.map(emoji => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => setNewType({ ...newType, icone: emoji })}
                    className={`w-8 h-8 rounded hover:bg-gray-100 ${newType.icone === emoji ? 'bg-blue-100 ring-2 ring-blue-400' : ''}`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Couleur</label>
              <div className="flex flex-wrap gap-1 p-2 border rounded-lg bg-white max-h-24 overflow-y-auto">
                {COULEURS_DISPONIBLES.map(c => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setNewType({ ...newType, couleur: c.value })}
                    className={`w-6 h-6 rounded-full ${newType.couleur === c.value ? 'ring-2 ring-offset-1 ring-blue-400' : ''}`}
                    style={{ backgroundColor: c.preview }}
                    title={c.label}
                  />
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
              <select
                value={newType.categorie}
                onChange={(e) => setNewType({ ...newType, categorie: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg text-sm"
              >
                {CATEGORIES.filter(c => c.value !== 'systeme').map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Preview */}
          <div className="mb-4 p-3 bg-white rounded-lg border">
            <span className="text-sm text-gray-500 mr-3">Aperçu:</span>
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-white ${newType.couleur}`}>
              <span>{newType.icone}</span>
              <span className="font-medium">{newType.label || 'Nouveau type'}</span>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowAddForm(false)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving === 'new'}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
            >
              {saving === 'new' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Créer
            </button>
          </div>
        </form>
      ) : (
        <button
          onClick={() => setShowAddForm(true)}
          className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-600 hover:border-green-400 hover:text-green-600 transition flex items-center justify-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Ajouter un type d'action
        </button>
      )}
    </div>
  );
};

// Composant pour éditer un type existant
const EditTypeForm = ({ type, onSave, onCancel, saving }) => {
  const [formData, setFormData] = useState({
    label: type.label,
    icone: type.icone,
    couleur: type.couleur,
    categorie: type.categorie
  });

  return (
    <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
      <h4 className="font-semibold text-gray-900 mb-4">✏️ Modifier: {type.label}</h4>
      
      <div className="grid grid-cols-4 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Libellé</label>
          <input
            type="text"
            value={formData.label}
            onChange={(e) => setFormData({ ...formData, label: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Icône</label>
          <select
            value={formData.icone}
            onChange={(e) => setFormData({ ...formData, icone: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg text-sm"
          >
            {EMOJIS.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Couleur</label>
          <select
            value={formData.couleur}
            onChange={(e) => setFormData({ ...formData, couleur: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg text-sm"
          >
            {COULEURS_DISPONIBLES.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
          <select
            value={formData.categorie}
            onChange={(e) => setFormData({ ...formData, categorie: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg text-sm"
          >
            {CATEGORIES.filter(c => c.value !== 'systeme').map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>
      </div>
      
      <div className="flex gap-2">
        <button onClick={onCancel} className="px-4 py-2 border text-gray-700 rounded-lg hover:bg-gray-50">
          Annuler
        </button>
        <button
          onClick={() => onSave(formData)}
          disabled={saving}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Enregistrer
        </button>
      </div>
    </div>
  );
};

export default WidgetSuiviConfig;
